import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ghlContactHref } from "@/lib/ghl-links";

interface OpenInGhlButtonProps {
  ghlContactId: string | null | undefined;
  ghlLocationId: string | null | undefined;
  /** Extra classes for sizing per surface (e.g. smaller in list rows). */
  className?: string;
}

/**
 * One-click jump from a quote or contact into the contact's GHL conversation —
 * it opens the GHL contact detail page (where the thread + composer live) in a
 * new tab. When the contact isn't synced to GHL yet, it renders disabled with a
 * tooltip rather than a broken link or blank tab (#380).
 */
export function OpenInGhlButton({ ghlContactId, ghlLocationId, className }: OpenInGhlButtonProps) {
  const href = ghlContactHref(ghlLocationId, ghlContactId);

  if (!href) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A span, not a disabled <button>: disabled controls don't fire the
              hover events Radix Tooltip needs, so the explainer wouldn't show. */}
          <span
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/40 cursor-not-allowed",
              className,
            )}
            aria-label="Not synced to GHL yet"
          >
            <MessageSquare className="h-4 w-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent>Not synced to GHL yet</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          asChild
          variant="ghost"
          size="icon"
          className={cn("h-7 w-7 text-muted-foreground hover:text-primary", className)}
          aria-label="Message in GHL"
        >
          <a href={href} target="_blank" rel="noopener noreferrer">
            <MessageSquare className="h-4 w-4" />
          </a>
        </Button>
      </TooltipTrigger>
      <TooltipContent>Message in GHL</TooltipContent>
    </Tooltip>
  );
}
